import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@hooks/useAuth';
import { ADMIN_ROLE, ADMIN_SCHOOL, ADMIN_CLASS, STUDENT_ROLE } from '@constants/roleconstants';

interface RoleGuardProps {
  children: React.ReactNode;
  allowedRoles?: string[]; // Array of allowed roles (e.g., ['admin'], ['admin', 'admin_school'])
  fallbackPath?: string; // Path to redirect if role is not allowed (default: '/admin/dashboard')
  redirectOnUnauthorized?: boolean; // Whether to redirect or show error (default: true)
}

const RoleGuard: React.FC<RoleGuardProps> = ({
  children,
  allowedRoles,
  fallbackPath = '/admin/dashboard',
  redirectOnUnauthorized = true,
}) => {
  const { auth, isAdmin, isAdminSchool, isTeacher, isStudent } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // If no allowedRoles specified, allow all authenticated users
    if (!allowedRoles || allowedRoles.length === 0) {
      return;
    }

    // Check if user is authenticated
    if (!auth?.access_token) {
      if (redirectOnUnauthorized) {
        navigate('/user/login');
      }
      return;
    }

    // Check if user's role is in the allowed roles
    const userRole = auth?.role;
    const isAllowed = allowedRoles.includes(userRole);

    if (!isAllowed && redirectOnUnauthorized) {
      // Determine appropriate fallback based on user role
      let defaultFallback = fallbackPath;
      let menuKey = 'dashboard';

      if (!defaultFallback || defaultFallback === '/admin/dashboard') {
        // Set default fallback based on user's actual role
        if (isAdmin) {
          defaultFallback = '/admin/dashboard';
          menuKey = 'dashboard';
        } else if (isAdminSchool) {
          defaultFallback = '/admin/dashboard';
          menuKey = 'dashboard';
        } else if (isTeacher) {
          defaultFallback = '/teacher/dashboard';
          menuKey = 'dashboard';
        } else if (isStudent) {
          defaultFallback = '/student/dashboard';
          menuKey = 'dashboard';
        } else {
          defaultFallback = '/user/login';
          menuKey = 'dashboard';
        }
      }

      // Update sessionStorage to set the correct menu key BEFORE navigation
      sessionStorage.setItem('selectedKey', menuKey);

      // Dispatch custom event to update menu highlighting immediately
      const menuKeyChangeEvent = new CustomEvent('menuKeyChange', {
        detail: { key: menuKey }
      });
      window.dispatchEvent(menuKeyChangeEvent);

      navigate(defaultFallback);
    }
  }, [auth?.role, auth?.access_token, allowedRoles, fallbackPath, redirectOnUnauthorized, navigate, isAdmin, isAdminSchool, isTeacher, isStudent]);

  // If no allowedRoles specified, render children (no restriction)
  if (!allowedRoles || allowedRoles.length === 0) {
    return <>{children}</>;
  }

  // Check if user is authenticated
  if (!auth?.access_token) {
    if (redirectOnUnauthorized) {
      return null; // Will redirect in useEffect
    }
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Unauthorized</h2>
          <p className="text-gray-600">Please log in to access this page.</p>
        </div>
      </div>
    );
  }

  // Check if user's role is allowed
  const userRole = auth?.role;
  const isAllowed = allowedRoles.includes(userRole);

  if (!isAllowed) {
    if (redirectOnUnauthorized) {
      return null; // Will redirect in useEffect
    }
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  // User is allowed, render children
  return <>{children}</>;
};

export default RoleGuard;

